import { create } from 'zustand';
import { authApi } from '../api/authApi';
import { logger } from '../utils/logger';
import { storageSet, storageRemove } from '../utils/storage';
import { telegram } from '../utils/telegram';
import type { SessionDto, SessionUserDto } from '../types/auth';
import type { LanguageCode } from '../types/i18n';
import { useLanguageStore } from './languageStore';

export type AuthStatus = 'idle' | 'pending' | 'authenticated' | 'error';
export type AuthErrorReason = 'NO_INIT_DATA' | 'NO_SLUG' | 'NETWORK' | 'AUTH_FAILED' | null;

interface AuthState {
  status: AuthStatus;
  errorReason: AuthErrorReason;
  session: SessionDto | null;
  /** Convenience accessor — derived from session.user */
  user: SessionUserDto | null;

  bootstrap: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (partial: Partial<SessionUserDto>) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  errorReason: null,
  session: null,
  user: null,

  /**
   * Bootstrap flow:
   * 1. status = 'pending' → SplashScreen visible
   * 2. Check initData — absent in plain browser → error: NO_INIT_DATA
   * 3. Token exists → GET /me/session (interceptor handles JWT_EXPIRED refresh)
   *    - 200 → cache tenantSlug, authenticated
   *    - network error → error: NETWORK
   *    - 401 (refresh also failed) → fall through to telegram-login
   *      (interceptor cleared tokens but did NOT dispatch auth:logout for /me/session)
   * 4. No token OR /me/session 401 → POST /telegram-login
   *    getBotUsername() uses cached tenantSlug (priority 3) — correct tenant context
   *    - success → cache tenantSlug, authenticated
   *    - fail → error: AUTH_FAILED or NETWORK
   */
  bootstrap: async () => {
    set({ status: 'pending', errorReason: null });

    const initData = telegram.getInitData();
    if (!initData) {
      logger.warn('bootstrap: no initData — not running in Telegram');
      set({ status: 'error', errorReason: 'NO_INIT_DATA' });
      return;
    }

    // ─── User switch detection ───────────────────────────────────────────
    // Telegram'da boshqa accountga o'tilsa (masalan, provider account dan
    // customer account ga), cached JWT eski user'niki bo'lib qoladi. Buni
    // aniqlash uchun Telegram user.id ni saqlaymiz va har bootstrap'da
    // solishtramiz. User o'zgargan bo'lsa — eski JWT va cache tozalanadi.
    const currentTgUser = telegram.getUser();
    const currentTgUserId = currentTgUser?.id?.toString() ?? null;
    const cachedTgUserId = localStorage.getItem('telegramUserId');
    if (currentTgUserId && cachedTgUserId && currentTgUserId !== cachedTgUserId) {
      logger.info('bootstrap: Telegram user changed — clearing tokens for re-auth', {
        cachedTgUserId,
        currentTgUserId,
      });
      await Promise.all([
        storageRemove('accessToken'),
        storageRemove('refreshToken'),
      ]);
      // Cached slug ham tozalanadi — yangi user'ning bot context'i boshqacha
      // bo'lishi mumkin (masalan, customer faqat tenant bot orqali ko'rinadi).
      localStorage.removeItem('tenantSlug');
    }

    // ─── Bot context switch detection ────────────────────────────────────
    // Foydalanuvchi boshqa bot orqali kirgan bo'lsa (platform → tenant yoki
    // teskari), URL'dagi slug cached slug'dan farq qiladi. Bu holda eski
    // tenant JWT'i tashlanadi va telegram-login yangi tenant uchun chaqiriladi.
    // (Telegram WebApp initData ichida bot username yo'q — shu sababli URL
    // signal'iga tayanamiz.)
    const urlSlug = telegram.getInitialBotUsername();
    const cachedSlug = localStorage.getItem('tenantSlug');
    if (urlSlug && cachedSlug && urlSlug !== cachedSlug) {
      logger.info('bootstrap: bot context changed — clearing tokens for re-auth', {
        cachedSlug,
        urlSlug,
      });
      await Promise.all([
        storageRemove('accessToken'),
        storageRemove('refreshToken'),
      ]);
      localStorage.setItem('tenantSlug', urlSlug);
    }

    const existingToken =
      sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');

    if (existingToken) {
      try {
        const slugBeforeCall = telegram.getBotUsername();
        logger.info('bootstrap: token found, fetching /me/session', {
          requestedSlug: slugBeforeCall,
        });
        const session = await authApi.getSession();
        // Cache tenantSlug so telegram-login re-auth uses the correct tenant
        localStorage.setItem('tenantSlug', session.tenantContext.tenantSlug);
        // Cache Telegram user ID for user-switch detection
        if (currentTgUserId) {
          localStorage.setItem('telegramUserId', currentTgUserId);
        }
        set({ status: 'authenticated', session, user: session.user });
        // Initialize language from tenant context + server-persisted user preference
        useLanguageStore.getState().initializeFromTenant(
          (session.tenantContext.supportedLanguages ?? ['uz', 'ru', 'en']) as LanguageCode[],
          (session.tenantContext.defaultLanguage ?? 'uz') as LanguageCode,
          session.user.preferredLanguage,
        );
        logger.info('bootstrap: /me/session success', {
          requestedSlug: slugBeforeCall,
          responseTenantSlug: session.tenantContext.tenantSlug,
          responseIsPlatformTenant: session.tenantContext.isPlatformTenant,
          routingTarget: session.routing.target,
          routingReason: session.routing.reason,
          primaryRole: session.primaryRole,
          roles: session.roles,
          ownedTenantsCount: session.ownedTenants.length,
        });
        return;
      } catch (sessionError: unknown) {
        const axErr = sessionError as { response?: { status?: number }; code?: string };
        const httpStatus = axErr?.response?.status;

        if (!httpStatus) {
          logger.error('bootstrap: /me/session network error', { code: axErr?.code });
          set({ status: 'error', errorReason: 'NETWORK' });
          return;
        }

        // 401: interceptor tried refresh — failed. Tokens cleared by interceptor.
        // Fall through to telegram-login. getBotUsername() will use cached tenantSlug
        // (priority 3 in telegram.ts), so the correct tenant context is preserved.
        logger.warn('bootstrap: /me/session 401, retrying via telegram-login', { httpStatus });
      }
    }

    // No token OR /me/session 401 → fresh telegram-login
    // Guard: slug must be determinable before attempting login.
    // Empty slug means user opened the URL directly, not via the bot button.
    const slug = telegram.getBotUsername();
    if (!slug) {
      logger.warn('bootstrap: tenantSlug unknown — user must open via bot button');
      set({ status: 'error', errorReason: 'NO_SLUG' });
      return;
    }

    try {
      logger.info('bootstrap: attempting telegram-login', { slug });
      const loginResult = await authApi.telegramLogin();
      // Cache tenantSlug for future re-auth after token expiry
      localStorage.setItem('tenantSlug', loginResult.session.tenantContext.tenantSlug);
      // Cache Telegram user ID for user-switch detection
      if (currentTgUserId) {
        localStorage.setItem('telegramUserId', currentTgUserId);
      }
      await Promise.all([
        storageSet('accessToken', loginResult.accessToken),
        storageSet('refreshToken', loginResult.refreshToken),
      ]);
      set({
        status: 'authenticated',
        session: loginResult.session,
        user: loginResult.session.user,
      });
      // Initialize language from tenant context + server-persisted user preference
      useLanguageStore.getState().initializeFromTenant(
        (loginResult.session.tenantContext.supportedLanguages ?? ['uz', 'ru', 'en']) as LanguageCode[],
        (loginResult.session.tenantContext.defaultLanguage ?? 'uz') as LanguageCode,
        loginResult.session.user.preferredLanguage,
      );
      logger.info('bootstrap: telegram-login success', {
        requestedSlug: slug,
        responseTenantSlug: loginResult.session.tenantContext.tenantSlug,
        responseIsPlatformTenant: loginResult.session.tenantContext.isPlatformTenant,
        routingTarget: loginResult.session.routing.target,
        routingReason: loginResult.session.routing.reason,
        primaryRole: loginResult.session.primaryRole,
        roles: loginResult.session.roles,
        ownedTenantsCount: loginResult.session.ownedTenants.length,
      });
    } catch (error: unknown) {
      const axErr = error as { response?: { status?: number }; code?: string };
      const hasResponse = !!axErr?.response?.status;

      logger.error('bootstrap: telegram-login failed', {
        status: axErr?.response?.status,
        code: axErr?.code,
      });

      set({
        status: 'error',
        errorReason: hasResponse ? 'AUTH_FAILED' : 'NETWORK',
      });
    }
  },

  logout: async () => {
    await authApi.logout();
    await Promise.all([
      storageRemove('accessToken'),
      storageRemove('refreshToken'),
    ]);
    // Clear identity caches so next login starts clean
    localStorage.removeItem('telegramUserId');
    localStorage.removeItem('tenantSlug');
    set({ status: 'idle', session: null, user: null, errorReason: null });
    logger.info('logout: session cleared');
  },

  setUser: (partial) => {
    set((s) => {
      if (!s.user) return s;
      const mergedUser: SessionUserDto = { ...s.user, ...partial };
      return {
        user: mergedUser,
        session: s.session ? { ...s.session, user: mergedUser } : null,
      };
    });
  },
}));
